import { Component, inject, input, OnInit, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BrokerService } from '@app/core/services/broker.service';
import { LocationService } from '@app/core/services/location.service';
import { ReservationFilter, ReservationStatus, ReservationStatusesDisplayNames } from '../reservations.model';
import { animate, style, transition, trigger } from '@angular/animations';
import { MultiSelectModule } from 'primeng/multiselect';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DatePickerModule } from 'primeng/datepicker';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BrokerAutocompleteDirective } from '@app/shared/directives/broker-autocomplete.directive';
import { ButtonModule } from 'primeng/button';
import { LocationMultiselectDirective } from '@app/shared/directives/location-multiselect.directive';
import { SelectModule } from 'primeng/select';

type SearchPredicateType = 'reservationNumber' | 'rentalAgreementNumber' | 'reference';

interface ReservationFilterForm {
  searchText: FormControl<string | null>;
  predicate: FormControl<SearchPredicateType | null>;
  driverFirstName: FormControl<string | null>;
  driverLastName: FormControl<string | null>;
  clientId: FormControl<{ code: number; selectedLabel: string } | null>;
  pickupLocations: FormControl<{ code: string }[] | null>;
  dropOffLocations: FormControl<{ code: string }[] | null>;
  dateOutRange: FormControl<(Date | null)[] | null>;
  dateInRange: FormControl<(Date | null)[] | null>;
  creationDateRange: FormControl<(Date | null)[] | null>;
  statuses: FormControl<ReservationStatus[] | null>;
}

@Component({
  selector: 'app-reservation-filter',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MultiSelectModule,
    IftaLabelModule,
    DatePickerModule,
    AutoCompleteModule,
    BrokerAutocompleteDirective,
    ButtonModule,
    LocationMultiselectDirective,
    SelectModule
  ],
  templateUrl: './reservation-filter.component.html',
  styleUrl: './reservation-filter.component.scss',
  animations: [
    trigger('advancedFilterAnimation', [
      transition(':enter', [
        style({ opacity: 0, height: '0' }),
        animate('200ms ease-out', style({ opacity: 1, height: '*' }))
      ]),
      transition(':leave', [animate('200ms ease-out', style({ height: '0', opacity: 0 }))])
    ]),
    trigger('searchBtnAnimation', [
      transition(':leave', [animate('200ms ease-out', style({ width: '0', opacity: 0 }))]),
      transition(':enter', [
        style({ opacity: 0, width: '0' }),
        animate('200ms ease-out', style({ width: '*', opacity: 1 }))
      ])
    ])
  ]
})
export class ReservationFilterComponent implements OnInit {
  private _locationService = inject(LocationService);
  private _brokerService = inject(BrokerService);

  initialState = input<ReservationFilter | null>(null);
  onFilterChange = output<ReservationFilter>();
  now = new Date();

  advancedFilter = false;
  form: FormGroup<ReservationFilterForm>;

  constructor() {
    this.form = new FormGroup<ReservationFilterForm>({
      searchText: new FormControl(null),
      predicate: new FormControl('reservationNumber'),
      driverFirstName: new FormControl(null),
      driverLastName: new FormControl(null),
      clientId: new FormControl(null),
      pickupLocations: new FormControl(null),
      dropOffLocations: new FormControl(null),
      dateOutRange: new FormControl(null),
      dateInRange: new FormControl(null),
      creationDateRange: new FormControl(null),
      statuses: new FormControl(null)
    });
  }

  ngOnInit(): void {
    const initValues = this.initialState();

    if (!initValues) {
      return;
    }

    const { rentalAgreementNumber, reservationNumber, reference, ...filterValues } = initValues;

    if (Object.keys(filterValues).length > 0) {
      this.advancedFilter = true;
    }

    const statuses = initValues.statuses
      ? initValues.statuses instanceof Array
        ? initValues.statuses.map((x) => Number(x) as ReservationStatus)
        : [Number(initValues.statuses) as ReservationStatus]
      : null;

    const pickupLocations = initValues.pickupLocations
      ? initValues.pickupLocations.split(',').map((x) => ({ code: x }))
      : null;

    const dropOffLocations = initValues.dropOffLocations
      ? initValues.dropOffLocations.split(',').map((x) => ({ code: x }))
      : [];

    const initFormValues = {
      predicate: 'reservationNumber' as SearchPredicateType,
      searchText: rentalAgreementNumber ?? null,
      driverFirstName: initValues.driverFirstName ?? null,
      driverLastName: initValues.driverLastName ?? null,
      clientId: null,
      pickupLocations: pickupLocations,
      dropOffLocations: dropOffLocations,
      dateOutRange: this.buildDateRange(initValues.dateOutFrom, initValues.dateOutTo),
      dateInRange: this.buildDateRange(initValues.dateInFrom, initValues.dateInTo),
      creationDateRange: this.buildDateRange(initValues.creationDateFrom, initValues.creationDateTo),
      statuses: statuses
    };

    if (!isNaN(Number(initValues.clientId))) {
      this._brokerService.getBrokerByCode(initValues.clientId!).subscribe((loc) => {
        this.form.controls.clientId.setValue({
          code: loc.code,
          selectedLabel: `${loc.code} - ${loc.name}`
        });
      });
    }

    if (reservationNumber) {
      initFormValues.searchText = reservationNumber;
      initFormValues.predicate = 'reservationNumber';
    } else if (reference) {
      initFormValues.searchText = reference;
      initFormValues.predicate = 'reference';
    }

    this.form.setValue(initFormValues);
    this.form.markAsDirty();
  }

  submitSearch(): void {
    const { searchText, predicate } = this.form.getRawValue();

    if (!predicate) {
      return;
    }

    const filterValues = {} as ReservationFilter;

    filterValues[predicate!] = searchText;

    this.onFilterChange.emit(filterValues);
  }

  submitFilter(): void {
    const {
      predicate,
      searchText,
      creationDateRange,
      dateOutRange,
      dateInRange,
      clientId,
      pickupLocations,
      dropOffLocations,
      ...formValues
    } = this.form.getRawValue();

    const filterValues: ReservationFilter = {
      ...formValues,
      creationDateFrom: this.formatDate(creationDateRange?.[0]),
      creationDateTo: this.formatDate(creationDateRange?.[1]),
      dateOutFrom: this.formatDate(dateOutRange?.[0]),
      dateOutTo: this.formatDate(dateOutRange?.[1]),
      dateInFrom: this.formatDate(dateInRange?.[0]),
      dateInTo: this.formatDate(dateInRange?.[1]),
      clientId: clientId?.code,
      pickupLocations: pickupLocations?.map((x) => x.code).join(','),
      dropOffLocations: dropOffLocations?.map((x) => x.code).join(',')
    };

    filterValues[predicate!] = searchText;

    this.onFilterChange.emit(filterValues as ReservationFilter);
  }

  reset(): void {
    this.form.reset({ predicate: 'reservationNumber' });
    this.onFilterChange.emit({});
  }

  expandFilter(): void {
    this.advancedFilter = true;
  }

  collapseFilter(): void {
    this.advancedFilter = false;
  }

  getStatusesOptions(): { label: string; value: ReservationStatus }[] {
    return Object.values(ReservationStatus)
      .filter((x) => !isNaN(Number(x)))
      .map((status) => ({
        label: ReservationStatusesDisplayNames[status as ReservationStatus],
        value: status as ReservationStatus
      }));
  }

  private formatDate(date: Date | null | undefined): string | null {
    if (!date) {
      return null;
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildDateRange(dateFrom: string | null | undefined, dateTo: string | null | undefined): Date[] | null {
    if (!dateFrom && !dateTo) {
      return null;
    }
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    return from && to ? [from, to] : from ? [from] : [];
  }
}
